import {
  cloneElement,
  forwardRef,
  isValidElement,
  useId,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cx } from './cx.js';
import { AlertIcon, ChevronDownIcon } from './icons.js';

/**
 * Champs de saisie. Au doigt : police de 16 px (pas de zoom automatique sur iOS), hauteur de 44 px ;
 * au pointeur fin : 14 px, hauteur de 34 px. Libellé toujours visible au-dessus, aide sous le libellé,
 * erreur sous le champ.
 */
export const controlClasses = cx(
  'block w-full min-h-(--ctl-md) rounded-xl border border-line-strong bg-surface px-3 py-1.5 text-[1rem] leading-6 text-ink shadow-xs pointer-fine:text-base pointer-fine:leading-5',
  'placeholder:text-ink-3 transition-[border-color,box-shadow] duration-150 ease-out',
  'hover:border-ink-3/70',
  'focus:border-brand-strong focus:outline-none focus:ring-2 focus:ring-brand-strong/20',
  'disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-3',
  'aria-[invalid=true]:border-danger aria-[invalid=true]:focus:ring-danger/20',
);

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className, ...rest },
  ref,
) {
  return <input ref={ref} className={cx(controlClasses, className)} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, rows = 3, ...rest }, ref) {
    return (
      <textarea
        ref={ref}
        rows={rows}
        className={cx(controlClasses, 'leading-normal', className)}
        {...rest}
      />
    );
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select(
  { className, children, ...rest },
  ref,
) {
  return (
    <div className="relative">
      <select
        ref={ref}
        className={cx(controlClasses, 'cursor-pointer appearance-none pr-9', className)}
        {...rest}
      >
        {children}
      </select>
      <ChevronDownIcon
        size={16}
        className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-ink-3"
      />
    </div>
  );
});

type ControlProps = {
  id?: string;
  required?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
};

/**
 * Champ complet : libellé, mention « obligatoire », aide, erreur. L'enfant (Input, Select, Textarea)
 * reçoit automatiquement `id`, `aria-invalid` et `aria-describedby`.
 *
 *   <Field label="E-mail" required hint="Adresse professionnelle" error={err}>
 *     <Input type="email" value={v} onChange={…} />
 *   </Field>
 */
export function Field({
  label,
  hint,
  error,
  required,
  className,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
  children: ReactElement<ControlProps>;
}) {
  const auto = useId();
  const id = (isValidElement(children) && children.props.id) || auto;
  const hintId = hint ? `${id}-aide` : undefined;
  const errorId = error ? `${id}-erreur` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={className}>
      <FieldLabel htmlFor={id} required={required}>
        {label}
      </FieldLabel>
      {hint ? (
        <p id={hintId} className="-mt-0.5 mb-1.5 text-xs text-ink-3 lg:text-sm">
          {hint}
        </p>
      ) : null}
      {cloneElement(children, {
        id,
        ...(required !== undefined ? { required } : {}),
        ...(error ? { 'aria-invalid': true } : {}),
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
      })}
      {error ? <FieldError id={errorId}>{error}</FieldError> : null}
    </div>
  );
}

export function FieldLabel({
  htmlFor,
  required,
  children,
}: {
  htmlFor?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} className="mb-1 block text-sm font-medium text-ink-2">
      {children}
      {required ? <span className="font-normal text-ink-3"> (obligatoire)</span> : null}
    </label>
  );
}

export function FieldError({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <p id={id} className="mt-1 flex items-start gap-1.5 text-sm font-medium text-danger-fg">
      <AlertIcon size={14} className="mt-[3px] shrink-0" />
      <span>{children}</span>
    </p>
  );
}
